import type { Observable } from 'rxjs'
import { env } from '@/infra/env'
import type { PrimeMemberByPublicIdFn } from '@/features/iam'
import type { Member } from '@/features/iam/members/Member'
import type { Job } from '@/features/jobs/Job'
import type { Id } from '@/infra/id'
import { CsDeskApi } from '@/features/relation-manager/api/relation-manager-api'
import { FlockApi } from '@/features/relation-manager/api/flock-api'
import { RelationManagerStore } from './RelationManagerStore'

/**
 * Options for {@link RelationManagerFeature}.
 */
export interface RelationManagerFeatureOptions {
  /**
   * Stream of the API token.
   */
  token$: Observable<string>

  /**
   * Delegate for priming the relation manager's member.
   */
  primeMemberByPublicId: PrimeMemberByPublicIdFn
}

/**
 * Fetches the relation manager assigned to the job, or null if there is none.
 */
export type FetchRelationManagerFn = (job: Job) => Promise<Member | null>

/**
 * Reactive getter for a job's relation manager. `undefined` means it has not
 * been fetched yet.
 */
export type GetRelationManagerFn = (jobId: Id) => Member | null | undefined

/**
 * The relation managers assigned to jobs, read from CS Desk or Flock.
 */
export type RelationManagerFeature = ReturnType<typeof RelationManagerFeature>

export function RelationManagerFeature(options: RelationManagerFeatureOptions) {
  const csDeskApi = new CsDeskApi({
    baseURL: env.CSDESK_API,
    token$: options.token$,
  })

  const flockApi = new FlockApi({
    baseURL: env.FLOCK_API,
    token$: options.token$,
  })

  const relationManagerStore = new RelationManagerStore(
    csDeskApi,
    flockApi,
    options.primeMemberByPublicId,
  )

  const fetchRelationManager: FetchRelationManagerFn = (job) =>
    relationManagerStore.fetchForJob(job)

  const getRelationManager: GetRelationManagerFn = (jobId) =>
    relationManagerStore.getForJob(jobId)

  return {
    // Public
    fetchRelationManager,
    getRelationManager,
  }
}
